import React, { useEffect, useState, useMemo } from "react";
import { FaUsers } from "react-icons/fa";
import CustomerRow from "../smallComponent/CustomerRow";
import TotalSummary from "../payment/TotalSummary";
import CustomerFilterBar from "../smallComponent/CustomerFilterBar";
import * as XLSX from "xlsx";
import { useCustomers } from "../context/CustomerContext";

export default function CustomerList() {
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  const { allCustomers, fetchCustomers, loading } = useCustomers();
  const pageSize = 30;

  // 🔹 Filter whenever customers or filter inputs change
  useEffect(() => {
    let result = allCustomers || [];

    // search filter
    if (search) {
      result = result.filter((c) =>
        (c.customer_name || "")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }

    // date filter
    if (startDate) {
      result = result.filter(
        (c) => new Date(c.done_date) >= new Date(startDate)
      );
    }
    if (endDate) {
      result = result.filter(
        (c) => new Date(c.done_date) <= new Date(endDate)
      );
    }

    // sirf active customers
    result = result.filter(
      (c) => c.status === 1 || c.status === 2 
    );

    setFilteredCustomers(result);
    setCurrentPage(1);
  }, [allCustomers, search, startDate, endDate]);

  // 🔹 Reset
  const resetFilter = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setFilteredCustomers(allCustomers || []);
    setCurrentPage(1);
  };


  const exportToExcel = () => {
    const data = filteredCustomers.map((c, i) => ({
      SN: i + 1,
      Name: c.customer_name || "",
      Phone: c.contact_number || "",
      Business: c.business_name || "",
      StartDate: c.project_start_date ? new Date(c.project_start_date).toLocaleDateString() : "",
      DoneDate: c.done_date ? new Date(c.done_date).toLocaleDateString() : "",
      Developer: c.main_developer_name || "",
      CoDeveloper: c.codeveloper_names || "",
      Salesman: c.sales_representative_name || "",
      Price: c.price || 0,
      Due: c.due_amount || 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    XLSX.writeFile(workbook, "CustomerList.xlsx");
  };

  // Summary
  const totalAmount = filteredCustomers.reduce((acc, c) => acc + parseFloat(c.price || 0), 0);
  const totalDue = filteredCustomers.reduce((acc, c) => acc + parseFloat(c.due_amount || 0), 0);
  const totalReceived = totalAmount - totalDue;

  // Pagination
  const totalPages = Math.ceil(filteredCustomers.length / pageSize);
  const indexOfLast = currentPage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentCustomers = useMemo(() => {
    return showAll
      ? filteredCustomers
      : filteredCustomers.slice(indexOfFirst, indexOfLast);
  }, [filteredCustomers, showAll, indexOfFirst, indexOfLast]);


  return (
    <div className="container mx-auto mt-6 px-4">
      <h2 className="text-2xl font-semibold text-blue-700 mb-4 flex items-center gap-2">
        <FaUsers /> Customer List
      </h2>

      <div className="mb-4 bg-white shadow rounded p-4">
        <CustomerFilterBar
          search={search}
          setSearch={setSearch}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onExport={exportToExcel}
          onReset={resetFilter}
        />
      </div>

      <div className="overflow-x-auto bg-white rounded shadow">
        <table className="min-w-full border-collapse border border-gray-200 table-auto">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="border border-gray-300 px-4 py-2">SN</th>
              <th className="border border-gray-300 px-4 py-2">Customer Info</th>
              <th className="border border-gray-300 px-4 py-2">Start Date</th>
              <th className="border border-gray-300 px-4 py-2">Sales / Developer</th>
              <th className="border border-gray-300 px-4 py-2">Work Details</th>
              <th className="border border-gray-300 px-4 py-2">Payment</th>
              <th className="border border-gray-300 px-4 py-2">Call</th>
              <th className="border border-gray-300 px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4 text-gray-500">
                  No customers found.
                </td>
              </tr>
            ) : (
              currentCustomers.map((customer, idx) => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  index={showAll ? idx : indexOfFirst + idx}
                  refresh={fetchCustomers}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div className="text-gray-600">
          {showAll
            ? `Showing all ${filteredCustomers.length} customers`
            : `Page ${currentPage} of ${totalPages}`}
        </div>
        <div className="flex gap-2">
          {!showAll && (
            <>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
                className={`px-3 py-1 border rounded ${currentPage === 1 ? "text-gray-400 border-gray-300 cursor-not-allowed" : "text-blue-600 border-blue-600 hover:bg-blue-100"}`}
              >
                ◀ Prev
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
                className={`px-3 py-1 border rounded ${currentPage === totalPages ? "text-gray-400 border-gray-300 cursor-not-allowed" : "text-blue-600 border-blue-600 hover:bg-blue-100"}`}
              >
                Next ▶
              </button>
            </>
          )}
          <button
            onClick={() => setShowAll(!showAll)}
            className={`px-3 py-1 rounded font-semibold ${showAll ? "bg-yellow-400 text-yellow-900" : "bg-blue-500 text-white hover:bg-blue-600"}`}
          >
            {showAll ? "🔁 Paginate" : "📄 Show All"}
          </button>
        </div>
      </div>

      <TotalSummary
        customers={filteredCustomers}
        totalAmount={totalAmount}
        totalDue={totalDue}
        totalReceived={totalReceived}
      />
    </div>
  );
}
